<template>
  <ChartCard :data-widget-id="widgetId" :id-for-aria="widgetId">
    <ChartHeader
      :label="widgetLabel"
      :widget-download-id="widgetId"
      tooltip-key="hiring-gender-distribution"
    />
    <ChartContent
      height="291.5px"
      :empty-data="emptyData || noWidgetData"
      :error="error"
      :loading="loading"
      type="doughnutChart"
      class="px-3 pt-4 pb-3"
    >
      <DoughnutChart :id="widgetId" :data="chartData" :options="chartOptions" :plugins="plugins" title="Hires by gender distribution" />
    </ChartContent>
  </ChartCard>
</template>

<script setup>
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import ChartContent from '@/components/insights/Chart/ChartContent.vue';
import DoughnutChart from '@/components/insights/Chart/DoughnutChart.vue';
import { computed } from 'vue';
import { useColors } from '@/composables/colors';
import { LegendSpacingPlugin } from '@/api/helpers/insights/chartPlugins';
import { donutChartTooltipPluginConfig, donutChartLegendPluginConfig, donutChartScalesOptions } from '@/api/helpers/insights/commonChartConfigs';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  emptyData: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const widgetLabel = 'What is the gender distribution of our hires?';

const plugins = [LegendSpacingPlugin];

const widgetId = 'gender-distribution-of-hires';

const {
  candidateFemaleInferred,
  candidateOther,
  candidateMaleInferred,
} = useColors();

const colors = [
  candidateMaleInferred.value,
  candidateFemaleInferred.value,
  candidateOther.value,
];

const noWidgetData = computed(() => {
  const { gender } = props.data || {};
  return !gender || (!gender?.male && !gender?.female && !gender?.unknown);
});

const chartData = computed(() => {
  const { gender } = props.data || {};
  const { male, female, unknown } = gender || {};

  const total = male + female + unknown;

  const genders = [
    { label: 'Male', value: male },
    { label: 'Female', value: female },
    { label: 'Unknown', value: unknown },
  ];

  const filteredGenders = genders.filter((genderData) => genderData.value);

  return {
    labels: filteredGenders.map((genderData) => genderData.label),
    datasets: [
      {
        label: '',
        backgroundColor: colors.slice(0, filteredGenders.length),
        borderColor: colors.slice(0, filteredGenders.length),
        data: filteredGenders.map((genderData) => (genderData.value / total) * 100),
      },
    ],
  };
});

const chartOptions = computed(() => ({
  maintainAspectRatio: false,
  responsive: true,
  aspectRatio: 1,
  plugins: {
    tooltip: donutChartTooltipPluginConfig(chartData, widgetId),
    legend: donutChartLegendPluginConfig(colors),
  },
  scales: donutChartScalesOptions,
}));
</script>
